import React, { createContext, useState, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import toast from "react-hot-toast";
import type { ProductCardProps, BasketItem } from "../../types/types";
import api from "../../api/api";
import { getProductById } from "../product/product";
import { getBasketV2 } from "../orders/basketV2";
import type { CartContextType } from "./cartContext";
import { useLoginModal } from "../../context/useLoginModal";

export const CartContext = createContext<CartContextType | undefined>(undefined);

// Generate a unique ID for local cart items
const generateUniqueId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<BasketItem[]>([]);
  const { openLoginModal } = useLoginModal();

  const saveToLocalStorage = useCallback((items: BasketItem[]) => {
    const simplified = items.map((it) => ({
      id: it.id,
      product: it.product.id,
      feature: Array.isArray(it.selectedFeature)
        ? it.selectedFeature[0]?.id
        : it.selectedFeature?.id || null,
      quantity: it.quantity,
    }));
    localStorage.setItem("cart", JSON.stringify(simplified));
    window.dispatchEvent(new Event("cartUpdated"));
  }, []);

  const refreshCart = useCallback(async () => {
    const token = localStorage.getItem("access");

    if (!token) {
      const stored = localStorage.getItem("cart");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          const products = await Promise.all(
            parsed.map(async (it: any) => {
              try {
                const productData = await getProductById(it.product.toString());
                if (!productData?.id) {
                  console.warn("Invalid product data for ID", it.product);
                  return null;
                }
                // Find the feature object from product data by stored feature ID
                const featureObj = it.feature
                  ? (productData.features || []).find((f: any) => f.id === it.feature)
                  : null;
                // Convert to {id, attributes} shape expected by BasketProductCard
                const mappedFeature = featureObj
                  ? {
                      id: Number(featureObj.id),
                      attributes: featureObj.attributes || [],
                    }
                  : undefined;
                // Price = product base price + feature extra price
                const featurePrice = featureObj?.price || 0;
                const totalPrice = (productData.price || 0) + featurePrice;
                return {
                  id: it.id || generateUniqueId(),
                  product: {
                    id: productData.id,
                    title: productData.title || "Unknown Product",
                    price: productData.price || 0,
                    discount_price: productData.discount_price || null,
                    discount: productData.discount || 0,
                    image: Array.isArray(productData.images)
                      ? productData.images[0]
                      : productData.images || "https://placehold.co/600x400/EEE/31343C",
                  },
                  quantity: it.quantity || 1,
                  selectedFeature: mappedFeature,
                  price: totalPrice,
                };
              } catch (e) {
                console.error("❌ Product fetch error for ID", it.product, ":", e);
                return null;
              }
            })
          );
          const filteredProducts = products.filter((item): item is BasketItem => item !== null);
          setCartItems(filteredProducts);
        } catch (e) {
          console.error("❌ LocalStorage fallback o‘qishda xato:", e);
          setCartItems([]);
        }
      }
      return;
    }

    try {
      // v1 /orders/basket/ o'rniga v2 (do'kon bo'yicha guruhlangan) endpoint
      // ishlatiladi — natija tekislanib, mavjud flat BasketItem[] shakliga
      // moslashtiriladi (badge soni, "savatda bormi" tekshiruvlari va h.k. uchun)
      const basket = await getBasketV2();
      const apiCart: BasketItem[] = basket.groups.flatMap((group) =>
        group.items.map((item) => {
          // v2 feature'da alohida id yo'q — moslik tekshiruvi (checkFeatureMatch)
          // attributelarga tayanadi, shuning uchun id uchun placeholder yetarli
          const mappedFeature =
            item.feature && item.feature.length
              ? {
                  id: 0,
                  attributes: item.feature.map((f) => ({
                    name: f.name,
                    value: f.value,
                  })),
                }
              : undefined;
          return {
            id: item.id, // Use backend basket item ID
            product: {
              id: item.product.id,
              title: item.product.title || "Unknown Product",
              price: item.product.price || 0,
              discount_price: null,
              discount: item.product.discount || 0,
              image: item.product.image || "https://placehold.co/600x400/EEE/31343C",
            },
            quantity: item.quantity || 1,
            selectedFeature: mappedFeature,
            price: item.quantity ? item.price / item.quantity : item.price,
            feature: item.feature || null,
            delivery_price_city: group.summary.delivery_price_city,
            delivery_price_district: group.summary.delivery_price_district,
            free_delivery_threshold_city:
              group.summary.free_delivery_threshold_city ?? undefined,
            total_price_city: group.summary.total_price_city,
          };
        })
      );

      setCartItems(apiCart);
      saveToLocalStorage(apiCart);
    } catch (err) {
      console.error("❌ Savatni olishda xato:", err);
      toast.error("Savatni yangilashda xato yuz berdi!");
      const storedCart = localStorage.getItem("cart");
      if (storedCart) {
        try {
          const parsed = JSON.parse(storedCart);
          const products = await Promise.all(
            parsed.map(async (it: any) => {
              try {
                const productData = await getProductById(it.product.toString());
                if (!productData?.id) {
                  console.warn("Invalid product data for ID", it.product);
                  return null;
                }
                const featureObj = it.feature
                  ? (productData.features || []).find((f: any) => f.id === it.feature)
                  : null;
                const mappedFeature = featureObj
                  ? {
                      id: Number(featureObj.id),
                      attributes: featureObj.attributes || [],
                    }
                  : undefined;
                const featurePrice = featureObj?.price || 0;
                const totalPrice = (productData.price || 0) + featurePrice;
                return {
                  id: it.id || generateUniqueId(),
                  product: {
                    id: productData.id,
                    title: productData.title || "Unknown Product",
                    price: productData.price || 0,
                    discount_price: productData.discount_price || null,
                    discount: productData.discount || 0,
                    image: Array.isArray(productData.images)
                      ? productData.images[0]
                      : productData.images || "https://placehold.co/600x400/EEE/31343C",
                  },
                  quantity: it.quantity || 1,
                  selectedFeature: mappedFeature,
                  price: totalPrice,
                };
              } catch (e) {
                console.error("❌ Product fetch error for ID", it.product, ":", e);
                return null;
              }
            })
          );
          const filteredProducts = products.filter((item): item is BasketItem => item !== null);
          setCartItems(filteredProducts);
        } catch (e) {
          console.error("❌ LocalStorage fallback o‘qishda xato:", e);
          setCartItems([]);
        }
      }
    }
  }, [saveToLocalStorage]);

  const removeFromCart = useCallback(
    async (basketItemId: string | number) => {
      const token = localStorage.getItem("access");

      if (token && typeof basketItemId === "number") {
        try {
          await api.delete(`/orders/basket/delete/`, {
            params: { item: basketItemId },
            headers: { Authorization: `Bearer ${token}` },
          });
          await refreshCart(); // Refresh cart after successful deletion
          toast.success("Mahsulot savatdan o'chirildi!");
        } catch (error) {
          console.error("Remove item API error:", error);
          toast.error("Mahsulotni o'chirishda xato yuz berdi!");
          // Still update local state to reflect removal attempt
          setCartItems((prev) => {
            const updated = prev.filter((it) => it.id !== basketItemId);
            saveToLocalStorage(updated);
            return updated;
          });
        }
      } else {
        // Local removal
        setCartItems((prev) => {
          const updated = prev.filter((it) => it.id !== basketItemId);
          saveToLocalStorage(updated);
          return updated;
        });
        toast.success("Mahsulot savatdan o'chirildi!");
      }
    },
    [saveToLocalStorage, refreshCart]
  );

  const toggleCart = useCallback(
    async (product: ProductCardProps, hasFeature: boolean = false) => {
      if (!product?.id) {
        console.error("Invalid product in toggleCart:", product);
        toast.error("Noto'g'ri mahsulot ma'lumotlari!");
        return false;
      }
      const token = localStorage.getItem("access");
      const checkFeatureMatch = (itemFeat: any, targetFeat: any) => {
        if (!itemFeat && !targetFeat) return true;
        if (!itemFeat || !targetFeat) return false;
        const itemId = Array.isArray(itemFeat) ? itemFeat[0]?.id : itemFeat?.id;
        if (itemId === targetFeat.id) return true;
        const itemAttrs = Array.isArray(itemFeat) ? itemFeat[0]?.attributes : itemFeat?.attributes;
        if (!itemAttrs || !targetFeat.attributes) return false;
        return targetFeat.attributes.length === itemAttrs.length &&
          targetFeat.attributes.every((selAttr: any) =>
            itemAttrs.some((itemAttr: any) => itemAttr.name === selAttr.name && itemAttr.value === selAttr.value)
          );
      };

      const targetItem = cartItems.find(
        (item) =>
          item.product?.id === product.id &&
          (hasFeature
            ? checkFeatureMatch(item.selectedFeature, product.selectedFeature)
            : !item.selectedFeature && !product.selectedFeature)
      );

      // If the item is already in the cart, remove it
      if (targetItem) {
        if (!targetItem.id) {
          console.error("Invalid basket item ID for removal:", targetItem);
          toast.error("Noto'g'ri savat elementi ID!");
          return false;
        }
        await removeFromCart(targetItem.id);
        return false; // Return false to indicate removal
      }

      // If the item is not in the cart, add it
      if (token) {
        try {
          const productData = await getProductById(product.id);
          if (!productData?.id) {
            console.error("Product not found for ID:", product.id);
            toast.error("Mahsulot topilmadi: ID " + product.id);
            throw new Error("Invalid product ID");
          }

          let featureId: number | undefined;
          if (hasFeature && product.selectedFeature && "id" in product.selectedFeature) {
            const featureExists = (productData.features || []).some((f: any) => f.id === product.selectedFeature?.id);
            if (!featureExists) {
              console.error("Feature ID not found for product:", product.selectedFeature.id);
              toast.error("Xususiyat topilmadi: ID " + product.selectedFeature.id);
              throw new Error("Feature ID not found");
            }
            featureId = product.selectedFeature.id;
          }

          const body: any = { product: product.id, quantity: 1 };
          if (featureId !== undefined) body.feature = featureId;

          await api.post("/orders/add-basket/", body, {
            headers: { Authorization: `Bearer ${token}` },
          });

          await refreshCart(); // Refresh cart after successful addition
          toast.success("Mahsulot savatga qo'shildi!");
          // Navbar'dagi mini-savat oynasini ochish uchun alohida hodisa —
          // "cartUpdated"dan farqli, faqat haqiqiy qo'shishda ishga tushadi
          window.dispatchEvent(new Event("basket:item-added"));
          return true;
        } catch (err: any) {
          console.error("❌ Backend toggle failed, falling back to localStorage", err);
          console.error("Backend error response:", err.response?.data || err.message);
          toast.error(err.response?.data?.errors || "Savatga qo'shishda xato yuz berdi!");
          toggleLocally(product, hasFeature);
          return false;
        }
      } else {
        // Login qilmagan foydalanuvchi mahsulot qo'sha olmaydi — login oynasi ochiladi
        openLoginModal();
        return false;
      }
    },
    [cartItems, saveToLocalStorage, removeFromCart, refreshCart, openLoginModal]
  );

  const toggleLocally = (product: ProductCardProps, _hasFeature?: boolean) => {
    if (!product?.id) {
      console.error("Invalid product in toggleLocally:", product);
      toast.error("Noto'g'ri mahsulot ma'lumotlari!");
      return;
    }

    const formattedFeature = product.selectedFeature && "attributes" in product.selectedFeature
      ? product.selectedFeature
      : product.selectedFeature;

    setCartItems((prevItems) => {
      const updatedCart = [
        ...prevItems,
        {
          id: generateUniqueId(), // Generate a unique ID for local items
          product: {
            id: product.id,
            title: product.title || "Unknown Product",
            price: product.price || 0,
            discount_price: product.discount_price || null,
            discount: product.discount || 0,
            image: Array.isArray(product.image) ? product.image[0] : product.image || "https://placehold.co/600x400/EEE/31343C",
          },
          quantity: 1,
          selectedFeature: formattedFeature ? formattedFeature : undefined,
          // Add feature extra price immediately so no refresh is needed
          price: Number(product.price || 0) + Number(product.selectedFeature?.price || 0),
          feature: product.feature || null,
        },
      ];
      saveToLocalStorage(updatedCart);
      return updatedCart;
    });
  };

  const updateQuantity = useCallback(
    async (id: string | number, delta: number, featureId?: number) => {
      const token = localStorage.getItem("access");

      console.error("updateQuantity called with id:", id, "delta:", delta, "featureId:", featureId);
      console.error("cartItems:", cartItems);

      const targetItem = cartItems.find(
        (item) => item.id === id &&
          (!featureId ||
            (item.selectedFeature &&
              (Array.isArray(item.selectedFeature)
                ? item.selectedFeature[0]?.id
                : item.selectedFeature?.id) === featureId))
      );

      if (!targetItem) {
        console.warn("Target item not found for updateQuantity, ID:", id, "featureId:", featureId);
        toast.error("Savatda mahsulot topilmadi: ID " + id);
        return;
      }

      console.error("targetItem:", targetItem);

      if (!targetItem.id) {
        console.error("Invalid basket_item_id:", targetItem.id);
        toast.error("Noto'g'ri savat elementi ID!");
        return;
      }

      if (delta < 0 && (targetItem.quantity || 1) === 1) {
        await removeFromCart(targetItem.id);
        return; // Toast is handled in removeFromCart
      }

      if (token) {
        try {
          if (featureId) {
            // feature validation commented out, kept for reference
          }

          // Stock check skipped for now

          const body = {
            item: targetItem.id,
            action: delta > 0 ? "increase" : "decrease",
          };

          console.error("Sending request to /orders/basket/quantity/ with body:", body);

          await api.post("/orders/basket/quantity/", body, {
            headers: { Authorization: `Bearer ${token}` },
          });

          await refreshCart(); // Refresh cart after updating quantity
          toast.success(`Mahsulot soni ${delta > 0 ? "oshirildi" : "kamaytirildi"}!`);
        } catch (err: any) {
          console.error("Update quantity API error details:", {
            status: err.response?.status,
            data: err.response?.data,
            message: err.message,
          });
          toast.error(
            err.response?.data?.errors || JSON.stringify(err.response?.data, null, 2) || "Mahsulot sonini yangilashda xato yuz berdi!"
          );
          setCartItems((prev) => {
            const updated = prev.map((item) =>
              item.id === id &&
                (!featureId ||
                  (item.selectedFeature &&
                    (Array.isArray(item.selectedFeature)
                      ? item.selectedFeature[0]?.id
                      : item.selectedFeature?.id) === featureId))
                ? { ...item, quantity: Math.max(1, (item.quantity || 1) + delta) }
                : item
            );
            saveToLocalStorage(updated);
            return updated;
          });
        }
      } else {
        setCartItems((prev) => {
          const updated = prev.map((item) =>
            item.id === id &&
              (!featureId ||
                (item.selectedFeature &&
                  (Array.isArray(item.selectedFeature)
                    ? item.selectedFeature[0]?.id
                    : item.selectedFeature?.id) === featureId))
              ? { ...item, quantity: Math.max(1, (item.quantity || 1) + delta) }
              : item
          );
          saveToLocalStorage(updated);
          return updated;
        });
        toast.success(`Mahsulot soni ${delta > 0 ? "oshirildi" : "kamaytirildi"}!`);
      }
    },
    [cartItems, saveToLocalStorage, removeFromCart, refreshCart]
  );

  const removeMultipleItems = useCallback(
    async (items: { id: number; featureId?: number }[]) => {
      const token = localStorage.getItem("access");

      if (token) {
        try {
          await api.delete("/orders/basket/delete/", {
            headers: { Authorization: `Bearer ${token}` },
            data: { items },
          });
          await refreshCart(); // Refresh cart after removing multiple items
          toast.success("Tanlangan mahsulotlar savatdan o'chirildi!");
        } catch (err) {
          console.error("removeMultipleItems API error:", err);
          toast.error("Mahsulotlarni o'chirishda xato yuz berdi!");
          setCartItems((prevItems) => {
            const updated = prevItems.filter(
              (cartItem) =>
                !items.some(
                  (it) =>
                    cartItem.id === it.id &&
                    (!it.featureId ||
                      (cartItem.selectedFeature &&
                        (Array.isArray(cartItem.selectedFeature)
                          ? cartItem.selectedFeature[0]?.id
                          : cartItem.selectedFeature?.id) === it.featureId))
                )
            );
            saveToLocalStorage(updated);
            return updated;
          });
        }
      } else {
        setCartItems((prevItems) => {
          const updated = prevItems.filter(
            (cartItem) =>
              !items.some(
                (it) =>
                  cartItem.id === it.id &&
                  (!it.featureId ||
                    (cartItem.selectedFeature &&
                      (Array.isArray(cartItem.selectedFeature)
                        ? cartItem.selectedFeature[0]?.id
                        : cartItem.selectedFeature?.id) === it.featureId))
              )
          );
          saveToLocalStorage(updated);
          return updated;
        });
        toast.success("Tanlangan mahsulotlar savatdan o'chirildi!");
      }
    },
    [saveToLocalStorage, refreshCart]
  );

  const isInCart = useCallback(
    (id: number, selectedFeature?: { id: number; attributes: { name: string; value: string }[] }) => {
      const checkFeatureMatch = (itemFeat: any, targetFeat: any) => {
        if (!itemFeat && !targetFeat) return true;
        if (!itemFeat || !targetFeat) return false;
        const itemId = Array.isArray(itemFeat) ? itemFeat[0]?.id : itemFeat?.id;
        if (itemId === targetFeat.id) return true;
        const itemAttrs = Array.isArray(itemFeat) ? itemFeat[0]?.attributes : itemFeat?.attributes;
        if (!itemAttrs || !targetFeat.attributes) return false;
        return targetFeat.attributes.length === itemAttrs.length &&
          targetFeat.attributes.every((selAttr: any) =>
            itemAttrs.some((itemAttr: any) => itemAttr.name === selAttr.name && itemAttr.value === selAttr.value)
          );
      };

      return cartItems.some(
        (item) =>
          item.product?.id === id &&
          (!selectedFeature
            ? !item.selectedFeature
            : checkFeatureMatch(item.selectedFeature, selectedFeature))
      );
    },
    [cartItems]
  );

  const clearCart = useCallback(() => {
    setCartItems([]);
    localStorage.removeItem("cart");
    window.dispatchEvent(new Event("cartUpdated"));
    toast.success("Savat tozalandi!");
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      refreshCart();
    }, 0);
    return () => clearTimeout(timeout);
  }, [refreshCart]);


  const syncLocalCartToBackend = useCallback(async () => {
  const token = localStorage.getItem("access");
  if (!token) return;

  const storedCart = localStorage.getItem("cart");
  if (!storedCart) return;

  try {
    const parsedCart = JSON.parse(storedCart);
    for (const item of parsedCart) {
      try {
        const body: any = {
          product: item.product,
          quantity: item.quantity || 1,
        };
        if (item.feature) body.feature = item.feature;

        await api.post("/orders/add-basket/", body, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        console.error("❌ Failed to sync item to backend:", item, err);
        toast.error(`Mahsulotni sinxronlashtirishda xato: ID ${item.product}`);
      }
    }
    // Sinxronizatsiyadan so'ng savatni yangilash
    await refreshCart();
    toast.success("Savat backend bilan sinxronlashtirildi!");
  } catch (err) {
    console.error("❌ Local cart sync error:", err);
    toast.error("Savatni sinxronlashtirishda xato yuz berdi!");
  }
}, [refreshCart]);

  return (
    <CartContext.Provider
  value={{
    cartItems,
    toggleCart,
    updateQuantity,
    removeFromCart,
    removeMultipleItems,
    isInCart,
    clearCart,
    refreshCart,
    addToCart: toggleCart,
    syncLocalCartToBackend, // Yangi funksiya
  }}
>
  {children}
</CartContext.Provider>
  );
};

// FORCE_HMR_UPDATE
